import { useState } from 'react';
import { ExternalLink, Video as VideoIcon } from 'lucide-react';
import { toEmbed, detectPlatform } from '../lib/videoEmbed';

/**
 * VideoIntroPlayer — embeds a worker's video introduction (YouTube / Drive /
 * Vimeo / Dropbox) directly inside the parent view. Falls back to a clean
 * "Open in new tab" button for unrecognised hosts or when the embed errors.
 *
 *   <VideoIntroPlayer url="https://..." platform="youtube" />
 *
 * Renders nothing if `url` is missing — call sites can pass through without
 * extra null checks.
 */
export default function VideoIntroPlayer({ url, platform, showLabel = true, testIdPrefix = 'video-intro' }) {
    const [embedFailed, setEmbedFailed] = useState(false);
    if (!url) return null;

    const detected = platform || detectPlatform(url);
    const { embedUrl, kind } = toEmbed(url, detected);

    const openInNewTab = (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900 hover:underline mt-2"
            data-testid={`${testIdPrefix}-open-new-tab`}
        >
            Open in new tab <ExternalLink className="w-3 h-3" />
        </a>
    );

    const fallbackButton = (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white text-sm font-semibold px-4 h-10 hover:bg-zinc-800 transition-colors"
            data-testid={`${testIdPrefix}-watch-btn`}
        >
            <VideoIcon className="w-4 h-4" /> Watch Video Introduction
            <ExternalLink className="w-3 h-3" />
        </a>
    );

    const fallback = (
        <div className="border border-zinc-200 p-4 bg-zinc-50" data-testid={`${testIdPrefix}-fallback`}>
            <p className="text-xs text-zinc-500 mb-3">Video preview unavailable.</p>
            {fallbackButton}
        </div>
    );

    const playerBox = (() => {
        if (embedFailed || kind === 'link') {
            return fallback;
        }
        if (kind === 'iframe') {
            return (
                <div className="relative bg-black w-full" data-testid={`${testIdPrefix}-iframe-wrap`}>
                    <iframe
                        src={embedUrl}
                        title="Video Introduction"
                        className="w-full h-[250px] sm:h-[360px]"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        onError={() => setEmbedFailed(true)}
                        data-testid={`${testIdPrefix}-iframe`}
                    />
                </div>
            );
        }
        // kind === 'video' (Dropbox direct stream)
        return (
            <div className="bg-black w-full" data-testid={`${testIdPrefix}-video-wrap`}>
                <video
                    src={embedUrl}
                    controls
                    className="w-full max-h-[360px] object-contain"
                    onError={() => setEmbedFailed(true)}
                    data-testid={`${testIdPrefix}-video`}
                />
            </div>
        );
    })();

    return (
        <div data-testid={`${testIdPrefix}-section`}>
            {showLabel && (
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2 flex items-center gap-2">
                    <VideoIcon className="w-3.5 h-3.5" /> Video Introduction
                </p>
            )}
            {playerBox}
            {openInNewTab}
        </div>
    );
}
